import { clsx } from "clsx";
import { type FC, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { HStack, SymbolIcon } from "widgets";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { capitalizeFirstLetter, pluralizeWord } from "@/app-utils/string.js";
import type {
  CoordinateList,
  DeletedGroup,
  Group,
  Parent,
  ProjectActivity,
  ProjectFile,
  RegionFlag,
  View,
} from "@/graphql.js";
import { isDatasetFile } from "@/modules/files/utils/file.js";
import { getFlagRoute, getParentRoute, getViewRoute, getViewTitle } from "@/redux/modules/saved-view.js";
import { CONTENT_TYPES, ENTITY_TYPE } from "@/settings/settings.ts";
import { SmartLink } from "@/ui-modules/core/SmartLink.tsx";

import { TooltipController } from "../core/overlays/components/OverlayControllers.js";
import style from "./styles/project-activity.module.scss";

export interface IProjectActivityAttachmentsProps {
  activity: ProjectActivity;
  onDownloadProjectFile: (projectFile: ProjectFile) => void;
  onOpenDataset: (datasetId: number) => void;
  onLinkClicked?: () => void; // callback to detect when a lijnk has been clicked, so that we can proceed with some action e.g. dismiss the alert or close the notifications dropdown
}

interface IGenericProjectActivityDisplayParams {
  buildList: boolean;
  asAttachment: boolean;
  inaccessible: boolean;
  onDownloadProjectFile?: (projectFile: ProjectFile) => any;
  onOpenDataset?: (datasetId: number) => any;
}

const defaultProjectActivityDisplayParams: IGenericProjectActivityDisplayParams = {
  buildList: true,
  asAttachment: true,
  inaccessible: false,
};

const ProjectActivityAttachments: FC<IProjectActivityAttachmentsProps> = (props) => {
  const { activity, onDownloadProjectFile, onOpenDataset } = props;

  if (
    activity.views.length +
      activity.flags.length +
      activity.groups.length +
      activity.deletedGroups.length +
      activity.projectFiles.length +
      activity.coordinateLists.length +
      activity.parents.length ===
    0
  ) {
    return null;
  }

  const projectActivityDisplayParams: IGenericProjectActivityDisplayParams = {
    ...defaultProjectActivityDisplayParams,
    inaccessible: !activity.project,
    onDownloadProjectFile: onDownloadProjectFile,
    onOpenDataset: onOpenDataset,
  };

  return (
    <div style={{ paddingTop: "10px" }}>
      {activity.views.length > 0 && displayViews(activity.views, projectActivityDisplayParams)}
      {activity.parents.length > 0 && displayParents(activity.parents, projectActivityDisplayParams)}
      {activity.flags.length > 0 && displayFlags(activity.flags, projectActivityDisplayParams)}
      {activity.projectFiles.length > 0 && displayProjectFiles(activity.projectFiles, projectActivityDisplayParams)}
      {activity.coordinateLists.length > 0 &&
        displayCoordinateLists(activity.coordinateLists, projectActivityDisplayParams)}
      {activity.groups.length + activity.deletedGroups.length > 0 &&
        displayGroups(activity.groups, activity.deletedGroups, projectActivityDisplayParams)}
    </div>
  );
};

function getSpanStyle(buildList: boolean): object {
  return buildList ? { display: "block" } : { display: "inline" };
}

function displayGroups(
  groups: Group[],
  deletedGroups: DeletedGroup[],
  projectActivityDisplayParams: IGenericProjectActivityDisplayParams,
): ReactElement {
  const { buildList, asAttachment, inaccessible } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (groups && groups.length > 0) {
    for (const group of groups) {
      const typeLabel: string = pluralizeWord(group.typeDisplayName, group.itemCount);

      const msg = (
        <HStack>
          {attachmentIcon}
          <div className={clsx(style.activityAttachment)}>
            {inaccessible ? (
              <TooltipController content={`You have no access to this ${group.typeDisplayName} group`}>
                {(tooltipTriggerProps) => (
                  <span className="inaccessible_resource" {...tooltipTriggerProps}>
                    <b>{group.name}</b> ({group.itemCount} {typeLabel})
                  </span>
                )}
              </TooltipController>
            ) : (
              <TooltipController content={`Go to this ${group.typeDisplayName} group`}>
                {(tooltipTriggerProps) => (
                  <Link
                    to={`/group/${group.id}`}
                    onPointerEnter={tooltipTriggerProps.onPointerEnter}
                    onPointerLeave={tooltipTriggerProps.onPointerLeave}
                    ref={tooltipTriggerProps.ref}
                  >
                    <b>{group.name}</b> ({group.itemCount} {typeLabel})
                  </Link>
                )}
              </TooltipController>
            )}
          </div>
        </HStack>
      );

      list.push(
        <span style={spanStyle} key={"group_" + group.id}>
          {msg}
        </span>,
      );
    }
  }
  if (deletedGroups && deletedGroups.length > 0) {
    for (const myDeletedGroup of deletedGroups) {
      const typeLabel: string = pluralizeWord(myDeletedGroup.typeDisplayName, myDeletedGroup.itemCount);
      list.push(
        <HStack>
          {attachmentIcon}
          <TooltipController content={`This ${myDeletedGroup.typeDisplayName} group has been deleted`}>
            {(tooltipTriggerProps) => (
              <span className="inaccessible_resource" {...tooltipTriggerProps}>
                <b>{myDeletedGroup.name}</b> ({myDeletedGroup.itemCount} {typeLabel})
              </span>
            )}
          </TooltipController>
        </HStack>,
      );
    }
  }

  if (!(groups && groups.length > 0) && !(deletedGroups && deletedGroups.length > 0)) {
    return <b>TODO: [MISSING_GROUP_DATA]</b>;
  } else {
    return <span>{list}</span>;
  }
}

function displayViews(views: View[], projectActivityDisplayParams: IGenericProjectActivityDisplayParams): ReactElement {
  const { buildList, asAttachment, inaccessible } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (views && views.length > 0) {
    for (const view of views) {
      if (!view.deleted) {
        const msg = (
          <HStack>
            {attachmentIcon}
            <div className={clsx(style.activityAttachment)}>
              {inaccessible ? (
                <TooltipController content={`You have no access to this view`}>
                  {(tooltipTriggerProps) => (
                    <span {...tooltipTriggerProps} className="inaccessible_resource">
                      <b>{view.name}</b> ({getViewTitle(view)})
                    </span>
                  )}
                </TooltipController>
              ) : (
                <TooltipController content={`Go to this view`}>
                  {(tooltipTriggerProps) => (
                    <Link
                      to={getViewRoute(view)}
                      onPointerEnter={tooltipTriggerProps.onPointerEnter}
                      onPointerLeave={tooltipTriggerProps.onPointerLeave}
                      ref={tooltipTriggerProps.ref}
                    >
                      <b>{view.name}</b> ({getViewTitle(view)})
                    </Link>
                  )}
                </TooltipController>
              )}
            </div>
          </HStack>
        );
        list.push(<HStack key={"view_" + view.id}>{msg}</HStack>);
      } else if (view.deleted) {
        list.push(
          <HStack key={"view_" + view.id}>
            {attachmentIcon}
            <TooltipController content={"This view has been deleted"}>
              {(tooltipTriggerProps) => (
                <span {...tooltipTriggerProps} className="inaccessible_resource">
                  <b>{view.name}</b> ({getViewTitle(view)})
                </span>
              )}
            </TooltipController>
          </HStack>,
        );
      }
    }
    return <span>{list}</span>;
  } else {
    return <b>TODO: [MISSING_VIEW_DATA]</b>;
  }
}

function displayParents(
  parents: Parent[],
  projectActivityDisplayParams: IGenericProjectActivityDisplayParams,
): ReactElement {
  const { buildList, asAttachment, inaccessible } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (parents && parents.length > 0) {
    for (const parent of parents) {
      // TODO deleted case
      const msg = (
        <HStack>
          {attachmentIcon}
          <div className={clsx(style.activityAttachment)}>
            {inaccessible ? (
              <TooltipController content={`You have no access to this parent`}>
                {(tooltipTriggerProps) => (
                  <span {...tooltipTriggerProps} className="inaccessible_resource">
                    <b>{parent.name}</b>
                  </span>
                )}
              </TooltipController>
            ) : (
              <TooltipController content={`Go to this parent`}>
                {(tooltipTriggerProps) => (
                  <Link
                    to={getParentRoute(parent)}
                    onPointerEnter={tooltipTriggerProps.onPointerEnter}
                    onPointerLeave={tooltipTriggerProps.onPointerLeave}
                    ref={tooltipTriggerProps.ref}
                  >
                    <b>{parent.name}</b>
                  </Link>
                )}
              </TooltipController>
            )}
          </div>
        </HStack>
      );
      list.push(
        <span style={spanStyle} key={"parent_" + parent.id}>
          {msg}
        </span>,
      );
    }
    return <span>{list}</span>;
  }
}

function displayFlags(
  flags: RegionFlag[],
  projectActivityDisplayParams: IGenericProjectActivityDisplayParams,
): ReactElement {
  const { buildList, asAttachment, inaccessible } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (flags && flags.length > 0) {
    for (const flag of flags) {
      if (!flag.deleted) {
        const msg = (
          <HStack>
            {attachmentIcon}
            <div className={clsx(style.activityAttachment)}>
              {inaccessible ? (
                <TooltipController content={"You have no access to this genome flag"}>
                  {(tooltipTriggerProps) => (
                    <span {...tooltipTriggerProps} className="inaccessible_resource">
                      <b>{flag.name}</b> ({capitalizeFirstLetter(flag.typeDisplayName)})
                    </span>
                  )}
                </TooltipController>
              ) : (
                <TooltipController content={"Go to this genome flag"}>
                  {(tooltipTriggerProps) => (
                    <Link
                      to={getFlagRoute(flag)}
                      onPointerEnter={tooltipTriggerProps.onPointerEnter}
                      onPointerLeave={tooltipTriggerProps.onPointerLeave}
                      ref={tooltipTriggerProps.ref}
                    >
                      <b>{flag.name}</b> ({capitalizeFirstLetter(flag.typeDisplayName)})
                    </Link>
                  )}
                </TooltipController>
              )}
            </div>
          </HStack>
        );
        list.push(
          <span style={spanStyle} key={"flag_" + flag.id}>
            {msg}
          </span>,
        );
      } else if (flag.deleted) {
        list.push(
          <TooltipController content={`This flag has been deleted and can no longer be opened in genome view`}>
            {(tooltipTriggerProps) => (
              <span style={spanStyle} key={"flag_" + flag.id} {...tooltipTriggerProps}>
                {attachmentIcon}
                <span className="inaccessible_resource">
                  <b>{flag.name}</b> ({capitalizeFirstLetter(flag.typeDisplayName)})
                </span>
              </span>
            )}
          </TooltipController>,
        );
      }
    }
    return <span>{list}</span>;
  } else {
    return <b>TODO: [MISSING_FLAG_DATA]</b>;
  }
}

// onDownloadProjectFile param can be missing if we are in the case of laying out the activity in the context of the project activity search engine (ProjectActivitySearchEngine.tsx)
function displayProjectFiles(
  projectFiles: ProjectFile[],
  projectActivityDisplayParams: IGenericProjectActivityDisplayParams,
): ReactElement {
  const { buildList, asAttachment, inaccessible, onDownloadProjectFile, onOpenDataset } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (projectFiles && projectFiles.length > 0) {
    for (const projectFile of projectFiles) {
      if (!projectFile.deleted) {
        const fileLinkAction = (event) => {
          event.stopPropagation();
          if (isDatasetFile(projectFile)) {
            onOpenDataset(projectFile.id);
          } else if (projectFile.contentType?.id !== CONTENT_TYPES.DIRECTORY) {
            onDownloadProjectFile(projectFile);
          }
        };
        const fileLinkTooltip = () => {
          if (isDatasetFile(projectFile)) {
            return "View in the Analytical Data Viewer";
          } else if (projectFile.contentType?.id === CONTENT_TYPES.DIRECTORY) {
            return "Navigate to this directory";
          } else {
            return "Download this project resource";
          }
        };

        const msg = (
          <HStack>
            {attachmentIcon}
            <div className={clsx(style.activityAttachment)}>
              {inaccessible || !onDownloadProjectFile ? (
                <TooltipController content={"You have no access to this resource"}>
                  {(tooltipTriggerProps) => (
                    <span {...tooltipTriggerProps} className="inaccessible_resource">
                      <b>{projectFile.fileName}</b> ({capitalizeFirstLetter(projectFile.typeDisplayName)})
                    </span>
                  )}
                </TooltipController>
              ) : projectFile.url ? (
                <TooltipController content={"Access this resource"}>
                  {(tooltipTriggerProps) => (
                    <a
                      onClick={(event) => event.stopPropagation()}
                      href={projectFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onPointerEnter={tooltipTriggerProps.onPointerEnter}
                      onPointerLeave={tooltipTriggerProps.onPointerLeave}
                      ref={tooltipTriggerProps.ref}
                    >
                      <b>{projectFile.fileName}</b> ({capitalizeFirstLetter(projectFile.typeDisplayName)})
                    </a>
                  )}
                </TooltipController>
              ) : (
                <TooltipController content={fileLinkTooltip()}>
                  {(tooltipTriggerProps) => {
                    const content = (
                      <span
                        onPointerEnter={tooltipTriggerProps.onPointerEnter}
                        onPointerLeave={tooltipTriggerProps.onPointerLeave}
                        ref={tooltipTriggerProps.ref}
                      >
                        <b>{projectFile.fileName}</b> ({capitalizeFirstLetter(projectFile.typeDisplayName)})
                      </span>
                    );
                    return projectFile.contentType?.id === CONTENT_TYPES.DIRECTORY ? (
                      <Link to={BrowserTab.createUrl(`/content/files/?file=${projectFile.id}`)}>{content}</Link>
                    ) : (
                      <a onClick={fileLinkAction}>{content}</a>
                    );
                  }}
                </TooltipController>
              )}
            </div>
          </HStack>
        );
        list.push(
          <span style={spanStyle} key={"file_" + projectFile.id}>
            {msg}
          </span>,
        );
      } else if (projectFile.deleted) {
        list.push(
          <HStack key={"file_" + projectFile.id}>
            {attachmentIcon}
            <TooltipController content={"This resource has been deleted"}>
              {(tooltipTriggerProps) => (
                <span {...tooltipTriggerProps} className="inaccessible_resource">
                  <b>{projectFile.fileName}</b> ({capitalizeFirstLetter(projectFile.typeDisplayName)})
                </span>
              )}
            </TooltipController>
          </HStack>,
        );
      }
    }
    return <span>{list}</span>;
  } else {
    return <b>TODO: [MISSING_PROJECT_FILE_DATA]</b>;
  }
}

function displayCoordinateLists(
  coordinateLists: CoordinateList[],
  projectActivityDisplayParams: IGenericProjectActivityDisplayParams,
): ReactElement {
  const { buildList, asAttachment, inaccessible } = projectActivityDisplayParams;
  const list: ReactElement[] = [];

  const spanStyle: object = getSpanStyle(buildList);
  const attachmentIcon: any = asAttachment ? (
    <SymbolIcon icon={"attachment"} style={{ margin: "3px", verticalAlign: "middle" }} />
  ) : null;

  if (coordinateLists && coordinateLists.length > 0) {
    for (const coordinateList of coordinateLists) {
      if (!coordinateList.deleted) {
        const msg = (
          <HStack>
            {attachmentIcon}
            <div className={clsx(style.activityAttachment)}>
              {inaccessible ? (
                <TooltipController content={"You have no access to this genome coordinateList"}>
                  {(tooltipTriggerProps) => (
                    <span {...tooltipTriggerProps} className="inaccessible_resource">
                      <b>{coordinateList.name}</b> ({capitalizeFirstLetter(coordinateList.typeDisplayName)})
                    </span>
                  )}
                </TooltipController>
              ) : coordinateList.organism?.id ? (
                <SmartLink
                  to={BrowserTab.createUrl(
                    `/organism/${coordinateList.organism.id}/genome?add_tracks=${
                      ENTITY_TYPE.COORDINATE
                    }&coordLists=${coordinateList.id}&showGrid=true`,
                  )}
                  tooltip={"Open this coordinate list in genome viewer"}
                  forceTooltip
                >
                  <b>{coordinateList.name}</b> ({capitalizeFirstLetter(coordinateList.typeDisplayName)})
                </SmartLink>
              ) : (
                <TooltipController
                  content={"This coordinate list is missing an organism and cannot be sent to the genome viewer."}
                >
                  {(tooltipTriggerProps) => (
                    <span {...tooltipTriggerProps} className="inaccessible_resource">
                      <b>{coordinateList.name}</b> ({capitalizeFirstLetter(coordinateList.typeDisplayName)})
                    </span>
                  )}
                </TooltipController>
              )}
            </div>
          </HStack>
        );
        list.push(
          <span style={spanStyle} key={"coordList_" + coordinateList.id}>
            {msg}
          </span>,
        );
      } else if (coordinateList.deleted) {
        list.push(
          <TooltipController
            content={`This coordinates list has been deleted and can no longer be opened in the genome viewer`}
          >
            {(tooltipTriggerProps) => (
              <span style={spanStyle} key={"coordList_" + coordinateList.id} {...tooltipTriggerProps}>
                {attachmentIcon}
                <span className="inaccessible_resource">
                  <b>{coordinateList.name}</b> ({capitalizeFirstLetter(coordinateList.typeDisplayName)})
                </span>
              </span>
            )}
          </TooltipController>,
        );
      }
    }
    return <span>{list}</span>;
  } else {
    return <b>TODO: [MISSING_COORDINATE_LIST_DATA]</b>;
  }
}

export default ProjectActivityAttachments;
export { ProjectActivityAttachments };
